import * as React from "react";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import { Button } from "components/button";
import { useTranslation } from "react-i18next";
import { enUS, es } from "date-fns/locale";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

type Props = {
  onDownload: (fromDate?: Date, toDate?: Date) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

export default function DownloadFilesModal({ onDownload, onCancel, isLoading }: Props) {
  const { t, i18n } = useTranslation();
  const tBase = "components.downloadFilesModal";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const locale = i18n.language === "es" ? es : enUS;

  const [downloadOption, setDownloadOption] = React.useState<"all" | "range">("all");
  const [fromDate, setFromDate] = React.useState<Date | null>(null);
  const [toDate, setToDate] = React.useState<Date | null>(null);

  const handleDownload = () => {
    if (downloadOption === "all") {
      onDownload();
    } else {
      onDownload(fromDate || undefined, toDate || undefined);
    }
  };

  const isDateRangeValid = () => {
    if (downloadOption === "all") return true;
    if (!fromDate || !toDate) return false;
    return fromDate <= toDate;
  };

  const getConfirmationText = () => {
    if (downloadOption === "all") {
      return tr("You will download all files.");
    }
    if (fromDate && toDate) {
      return tr("You will download files from {{fromDate}} to {{toDate}}.")
        .replace("{{fromDate}}", format(fromDate, "MM/dd/yyyy"))
        .replace("{{toDate}}", format(toDate, "MM/dd/yyyy"));
    }
    return tr("Please select a date range.");
  };

  return (
    <Container>
      <OptionsContainer>
        <OptionRow>
          <RadioInput
            type="radio"
            id="downloadAll"
            name="downloadOption"
            checked={downloadOption === "all"}
            onChange={() => setDownloadOption("all")}
          />
          <label htmlFor="downloadAll">{tr("Download All Files")}</label>
        </OptionRow>

        <OptionRow>
          <RadioInput
            type="radio"
            id="downloadRange"
            name="downloadOption"
            checked={downloadOption === "range"}
            onChange={() => setDownloadOption("range")}
          />
          <label htmlFor="downloadRange">{tr("Select Date Range")}</label>
        </OptionRow>

        <DatePickerContainer disabled={downloadOption === "all"}>
          <DatePickerWrapper>
            <DatePickerLabel>{tr("From")}</DatePickerLabel>
            <DatePicker
              selected={fromDate}
              onChange={(date: Date) => setFromDate(date)}
              disabled={downloadOption === "all"}
              locale={locale}
              dateFormat="MM/dd/yyyy"
              placeholderText={tr("Select date")}
              className="date-picker-input"
              maxDate={toDate || undefined}
            />
          </DatePickerWrapper>
          <DatePickerWrapper>
            <DatePickerLabel>{tr("To")}</DatePickerLabel>
            <DatePicker
              selected={toDate}
              onChange={(date: Date) => setToDate(date)}
              disabled={downloadOption === "all"}
              locale={locale}
              dateFormat="MM/dd/yyyy"
              placeholderText={tr("Select date")}
              className="date-picker-input"
              minDate={fromDate || undefined}
            />
          </DatePickerWrapper>
        </DatePickerContainer>
      </OptionsContainer>

      <ConfirmationText>{getConfirmationText()}</ConfirmationText>

      <InfoText>{tr("You will receive an email with a link to download the files once the export is ready. This process may take a few minutes to several hours depending on the amount of data requested. Please be patient.")}</InfoText>

      <ButtonContainer>
        <Button onClick={onCancel} disabled={isLoading}>
          {tr("Cancel")}
        </Button>
        <Button
          primary
          onClick={handleDownload}
          disabled={!isDateRangeValid() || isLoading}
          isLoading={isLoading}
        >
          {tr("Download")}
        </Button>
      </ButtonContainer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 10px;
  min-width: 350px;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 16px;
    cursor: pointer;
  }
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const DatePickerContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  gap: 16px;
  margin-left: 26px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? "none" : "auto"};

  .date-picker-input {
    width: 140px;
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #ff4f00;
    }

    &:disabled {
      background-color: #f5f5f5;
    }
  }
`;

const DatePickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DatePickerLabel = styled.span`
  font-size: 12px;
  color: #666;
`;

const ConfirmationText = styled.p`
  font-size: 14px;
  color: #555;
  padding: 12px;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin: 0;
`;

const InfoText = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  padding: 10px 12px;
  background-color: #e8f4fd;
  border-radius: 4px;
  border-left: 3px solid #2196f3;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
`;
